import { Button } from 'components/ui/button';
import { buttonStyle } from 'components/buttons/buttonStyle';
import SelectInput from 'components/forms/inputs/rhf-inputs/SelectInput';
import TextAreaInput from 'components/forms/inputs/rhf-inputs/TextAreaInput';
import TextInput from 'components/forms/inputs/rhf-inputs/TextInput';
import useAuth from 'hooks/useAuth.ts';
import { Control, FieldValues, FormProvider, useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { ClassifierData } from 'types/classifiers';
import { AddressDto, AddressForm } from 'types/contacts';
import { CustomField, CustomFieldDto } from 'types/customFields';
import { EntityType } from 'types/entity';
import { FeatureFlag } from 'types/features';
import { getClassifierOptionByCode, getValidOptionsFromClassifier } from 'utils/formUtil';
import { t } from 'utils/getTranslations';
import CustomFields from '../../forms/inputs/custom-fields/CustomFields';
import { useCustomFields } from '../../forms/inputs/custom-fields/useCustomFields';
import { ModalFooter } from '../ModalFooter';
import { ModalHeader } from '../ModalHeader';

interface Props {
    titleId?: string;
    onChange: (value: AddressDto) => void;
    onClose: () => void;
    initialData: AddressForm;
    classifiers: Record<string, ClassifierData>;
    customFields?: CustomField[];
    isBillingAddress?: boolean;
    sourceAddressForCopy?: AddressDto | null;
    partyType?: 'PERSON' | 'COMPANY';
}

const defaultValues: AddressForm = {
    address1: '',
    address2: null,
    cityName: '',
    zipCode: null,
    countryCode: null,
    additionalInformation: null,
};

const AddressModal = (props: Props) => {
    const {
        titleId = 'address-form.edit',
        initialData,
        classifiers,
        onClose,
        onChange,
        customFields,
        isBillingAddress,
        sourceAddressForCopy,
        partyType,
    } = props;
    const { user } = useAuth();
    const enabledFeatures = user?.enabledFeatures;
    const isAdditionalInformationEnabled =
        enabledFeatures && enabledFeatures.includes(FeatureFlag.ADDRESS_ADDITIONAL_INFORMATION);

    const methods = useForm<AddressForm>({
        defaultValues: initialData ? { ...defaultValues, ...initialData } : defaultValues,
        values: initialData as AddressForm,
    });

    const { customFields: addressCustomFields, mapCustomFieldsToDto: mapAddressCustomFieldsToDto } = useCustomFields({
        entityType: EntityType.PARTY_ADDRESS,
        fields: customFields,
    });
    const { handleSubmit, control, setValue } = methods;
    const handleOnChange = (data: AddressForm) => {
        onChange(mapFormDataToDto(data, mapAddressCustomFieldsToDto(data as unknown as Record<string, unknown>)));
        onClose();
    };
    const onSave = async () => {
        await handleSubmit((data) => handleOnChange(data))();
    };

    const copyFromSourceAddress = () => {
        if (sourceAddressForCopy) {
            setValue('address1', sourceAddressForCopy.address1 || '');
            setValue('address2', sourceAddressForCopy.address2 || '');
            setValue('cityName', sourceAddressForCopy.cityName || '');
            setValue('zipCode', sourceAddressForCopy.zipCode || '');
            setValue('additionalInformation', sourceAddressForCopy.additionalInformation || '');

            // Resolved through the unfiltered list: the source address is an existing record, so its
            // country has to copy across even when that value is no longer offered.
            const countryOption = getClassifierOptionByCode<string, string>(
                classifiers['COUNTRY'],
                sourceAddressForCopy.countryCode
            );
            if (countryOption) {
                setValue('countryCode', countryOption);
            }
        }
    };

    const shouldShowCopyLink =
        isBillingAddress &&
        sourceAddressForCopy &&
        (sourceAddressForCopy.address1 ||
            sourceAddressForCopy.address2 ||
            sourceAddressForCopy.cityName ||
            sourceAddressForCopy.zipCode ||
            sourceAddressForCopy.countryCode);

    const copyAddressLink = shouldShowCopyLink ? (
        <Button {...buttonStyle({ variant: 'outlined', color: 'primary' })} onClick={copyFromSourceAddress}>
            <FormattedMessage
                id={partyType === 'PERSON' ? 'address-form.copy-address' : 'address-form.copy-registration-address'}
            />
        </Button>
    ) : undefined;

    return (
        <FormProvider {...methods}>
            <ModalHeader title={t(titleId)} onCancel={onClose} />
            <div className="h-full overflow-y-auto px-8 py-[38px] max-sm:px-4 max-sm:py-6">
                <div className="flex flex-col gap-4">
                    <TextInput
                        name="address1"
                        control={control}
                        label="address-form.address-1"
                        placeholder="address-form.address-1"
                        required
                    />
                    <TextInput
                        name="address2"
                        control={control}
                        label="address-form.address-2"
                        placeholder="address-form.address-2"
                    />
                    <TextInput
                        name="cityName"
                        control={control}
                        label="address-form.city"
                        placeholder="address-form.city"
                        required
                    />
                    <div>
                        <div className="flex flex-row gap-4">
                            <div className="min-w-0 flex-1">
                                <TextInput
                                    name="zipCode"
                                    control={control}
                                    label="address-form.zip-code"
                                    placeholder="address-form.zip-code"
                                />
                            </div>
                            <div className="min-w-0 flex-1">
                                <SelectInput
                                    setValue={setValue}
                                    control={control}
                                    label="address-form.country"
                                    required
                                    name="countryCode"
                                    options={
                                        (getValidOptionsFromClassifier(classifiers['COUNTRY']) as {
                                            label: string;
                                            value: string;
                                        }[]) ?? []
                                    }
                                    getOptionLabel="label"
                                    getOptionIdentifier="value"
                                    placeholder="address-form.country"
                                />
                            </div>
                        </div>
                    </div>
                    {isAdditionalInformationEnabled ? (
                        <TextAreaInput
                            control={control}
                            name="additionalInformation"
                            label="address-form.additional-info"
                            placeholder="address-form.additional-info"
                            maxRows={5}
                            minRows={5}
                        />
                    ) : null}
                    {addressCustomFields && addressCustomFields.length > 0 ? (
                        <CustomFields
                            control={control as unknown as Control<FieldValues>}
                            fields={addressCustomFields}
                        />
                    ) : null}
                </div>
            </div>
            <ModalFooter
                onCancel={onClose}
                onSave={() => {
                    void onSave();
                }}
                extraButton={copyAddressLink}
            />
        </FormProvider>
    );
};

export default AddressModal;

const mapFormDataToDto = (address: AddressForm, customFields: CustomFieldDto[]): AddressDto => ({
    address1: address.address1,
    address2: address.address2,
    cityName: address.cityName,
    countryCode: address?.countryCode?.value ?? '',
    zipCode: address.zipCode,
    ...(customFields.length > 0 ? { customFields: customFields } : {}),
    additionalInformation: address.additionalInformation,
});
